import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Landmark, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const pick = (a) => ({ bank_id: a.bank_id, bank_name: a.bank_name, account_type: a.account_type, account_no: a.account_no, account_name: a.account_name, id: a.id });

export const AccountPicker = ({ banks, value, onSelect }) => {
  const [accounts, setAccounts] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (selectId) => {
    const { data } = await api.get("/wallet/accounts");
    setAccounts(data);
    const target = data.find((a) => a.id === selectId) || data[0];
    onSelect(target ? pick(target) : null);
    if (data.length === 0) setAdding(true);
  }, [onSelect]);

  useEffect(() => { load(); }, [load]);

  const remove = async (aid) => {
    try {
      await api.delete(`/wallet/accounts/${aid}`);
      toast.success("Rekening dihapus");
      load(value?.id === aid ? null : value?.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus rekening");
    }
  };

  return (
    <div data-testid="account-picker">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-gray-500">Rekening tujuan (bank / e-wallet)</span>
        {!adding && <button data-testid="account-add-toggle" onClick={() => setAdding(true)} className="flex items-center gap-1 text-[#0B4DA2] font-semibold"><Plus className="w-3.5 h-3.5" /> Tambah rekening</button>}
      </div>
      <div className="space-y-1.5">
        {accounts.map((a) => {
          const on = value?.id === a.id;
          return (
            <div key={a.id} data-testid={`account-item-${a.id}`} onClick={() => onSelect(pick(a))} className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-colors ${on ? "border-[#0B4DA2] bg-blue-50/60" : "border-gray-100"}`}>
              {on ? <CheckCircle2 className="w-4 h-4 text-[#0B4DA2] shrink-0" /> : <Circle className="w-4 h-4 text-gray-300 shrink-0" />}
              <Landmark className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold line-clamp-1">{a.bank_name}{a.account_type === "3" ? " (e-wallet)" : ""} • {a.account_no}</div>
                <div className="text-[11px] text-gray-400 line-clamp-1">a.n. {a.account_name}</div>
              </div>
              <button data-testid={`account-delete-${a.id}`} onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
      {adding && <AccountForm banks={banks} canCancel={accounts.length > 0} onCancel={() => setAdding(false)} onSaved={(id) => { setAdding(false); load(id); }} />}
    </div>
  );
};

const AccountForm = ({ banks, canCancel, onCancel, onSaved }) => {
  const [f, setF] = useState({ key: "", account_no: "", account_name: "" });
  const [saving, setSaving] = useState(false);
  const bkey = (b) => `${b.account_type}-${b.bank_id}`;
  const bank = banks.find((b) => bkey(b) === f.key) || banks[0];

  const save = async () => {
    if (!bank) return toast.error("Daftar bank belum dimuat");
    if (!f.account_no.trim() || !f.account_name.trim()) return toast.error("Lengkapi data rekening");
    setSaving(true);
    try {
      const { data } = await api.post("/wallet/accounts", { bank_id: String(bank.bank_id), bank_name: bank.bank_name, account_type: bank.account_type, account_no: f.account_no, account_name: f.account_name });
      toast.success("Rekening disimpan");
      onSaved(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan rekening");
    } finally {
      setSaving(false);
    }
  };

  const cls = "w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none bg-white";
  return (
    <div className="mt-2 p-3 rounded-xl bg-gray-50 space-y-2" data-testid="account-form">
      <select data-testid="wd-bank" value={bank ? bkey(bank) : ""} onChange={(e) => setF({ ...f, key: e.target.value })} className={cls}>
        {banks.length === 0 && <option value="">Memuat bank...</option>}
        {banks.map((b) => <option key={bkey(b)} value={bkey(b)}>{b.bank_name}{b.account_type === "3" ? " (e-wallet)" : ""}</option>)}
      </select>
      <input data-testid="wd-account-no" value={f.account_no} onChange={(e) => setF({ ...f, account_no: e.target.value })} placeholder="No. Rekening / No. HP e-wallet" className={cls} />
      <input data-testid="wd-account-name" value={f.account_name} onChange={(e) => setF({ ...f, account_name: e.target.value })} placeholder="Nama Pemilik Rekening" className={cls} />
      <div className="flex gap-2">
        {canCancel && <button data-testid="account-cancel" onClick={onCancel} className="flex-1 py-2 rounded-xl border border-gray-200 text-sm font-medium bg-white">Batal</button>}
        <button data-testid="account-save" onClick={save} disabled={saving} className="flex-1 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Rekening
        </button>
      </div>
    </div>
  );
};
